/*       */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Button, CircularProgress } from '@mic3/platform-ui';

import FormGenerator from 'app/containers/Designer/Form/components/FormGenerator';
import ModalDialog from 'app/components/organisms/ModalDialog/ModalDialog';
import { bind } from 'app/utils/decorators/decoratorUtils';
import { get } from 'app/utils/lo/lo';
import { createLlmProvider, updateLlmProvider } from 'store/actions/admin/llmProvidersActions';

// apiFormat values are lowercase on the backend; labels are shown capitalised.
const apiFormatOptions = [
    { value: 'google', label: 'Google' },
    { value: 'anthropic', label: 'Anthropic' },
];

const CancelButton = styled(Button)`
    margin-right: 8px !important;
`;

class AddLlmProvider extends PureComponent {
    static propTypes = {
        createLlmProvider: PropTypes.func.isRequired,
        updateLlmProvider: PropTypes.func.isRequired,
        onClose: PropTypes.func.isRequired,
        isLoading: PropTypes.bool,
        provider: PropTypes.object, // when set, the dialog edits this record instead of creating one
        records: PropTypes.array, // existing providers, used for the inline duplicate-name check
    };

    formRef = React.createRef();

    get isEdit() {
        return !!this.props.provider;
    }

    // Name (the record key, stored as `id`), apiFormat and apiKey are mandatory; baseUrl
    // is optional (defaults to the provider's official endpoint when empty). Name stays
    // editable when editing; uniqueness is validated inline (below) and re-checked in the
    // create/update actions against a fresh list.
    components = [
        {
            type: 'text',
            properties: { label: 'Name', name: 'id' },
            constraints: { required: true, custom: data => this.validateUniqueName(data) },
        },
        { type: 'text', properties: { label: 'Base URL', name: 'baseUrl' } },
        { type: 'text', properties: { label: 'API Key', name: 'apiKey' }, constraints: { required: true } },
        {
            type: 'typeahead',
            properties: { label: 'API Format', name: 'apiFormat', clearable: false, options: apiFormatOptions },
            constraints: { required: true },
        },
    ];

    // Inline duplicate-name check. An impossible pattern forces the format error to show
    // the message. When editing, the record's own name is excluded from the clash set.
    @bind
    validateUniqueName(data) {
        const name = data && data.id;
        if (!name) return {}; // let the `required` constraint handle empty
        const { records, provider } = this.props;
        const originalId = provider && provider.id;
        const clash = (records || []).some(p => p && p.id === name && p.id !== originalId);
        return clash ? { format: { pattern: /(?!)/, message: 'A provider with this name already exists.' } } : {};
    }

    @bind
    onFormSubmit(event) {
        event.preventDefault();
        this.formRef.current.isValidForm().then(({ data, errors }) => {
            if (errors) return;
            const { createLlmProvider, updateLlmProvider, provider, onClose } = this.props;
            // Name is editable, so pass the original id so the update matches the right record.
            const request = this.isEdit ? updateLlmProvider(data, provider.id) : createLlmProvider(data);
            request.then((result) => {
                if (result instanceof Error) return;
                onClose();
            });
        });
    }

    render() {
        const { isLoading, onClose, provider } = this.props;
        const isEdit = this.isEdit;
        return (
            <ModalDialog
                title={`${isEdit ? 'Edit' : 'Add'} LLM Provider`}
                onClose={onClose}
                withoutClose
                actions={
                    isLoading ? (
                        <CircularProgress size={24} color="primary" />
                    ) : (
                        <>
                            <CancelButton variant="text" onClick={onClose}>
                                Cancel
                            </CancelButton>
                            <Button onClick={this.onFormSubmit}>{isEdit ? 'Save' : 'Add'}</Button>
                        </>
                    )
                }
            >
                <FormGenerator components={this.components} data={provider} ref={this.formRef} />
            </ModalDialog>
        );
    }
}

export default connect(
    state => ({
        isLoading: get(state, 'admin.llmProviders.update.isLoading', false),
        records: get(state, 'admin.llmProviders.list.records', []),
    }),
    {
        createLlmProvider,
        updateLlmProvider,
    }
)(AddLlmProvider);
